import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Bell } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { secureFetch } from '../supabaseService';
import { cn } from '../lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';

const PAGE_SIZE = 50;

const CHANNEL_FILTERS = [
  { id: '', i18nKey: 'notifChannelAll' },
  { id: 'whatsapp', i18nKey: 'notifChannelWhatsapp' },
  { id: 'email', i18nKey: 'notifChannelEmail' },
  { id: 'push', i18nKey: 'notifChannelPush' },
  { id: 'sms', i18nKey: 'notifChannelSms' },
];

const ROLE_FILTERS = [
  { id: '', labelKey: 'notifChannelAll' },
  { id: 'owner', labelKey: 'portalOwner' },
  { id: 'operator', labelKey: 'operator' },
  { id: 'client', labelKey: 'client' },
];

function channelIcon(channel) {
  if (channel === 'whatsapp') return '📱';
  if (channel === 'sms') return '💬';
  if (channel === 'email') return '📧';
  if (channel === 'push') return '🔔';
  return '🔔';
}

function statusClass(status) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'failed') return 'text-de-accent';
  if (normalized === 'pending') return 'text-amber-400';
  return 'text-emerald-400';
}

function statusLabel(status, t) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'failed') return t('notifStatusFailed');
  if (normalized === 'pending') return t('notifStatusPending');
  return t('notifStatusSent');
}

function formatTime(iso) {
  const ms = new Date(iso).getTime();
  if (!Number.isFinite(ms)) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function truncateMessage(text, max = 120) {
  const value = String(text || '').trim();
  if (!value) return '—';
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1)}…`;
}

export default function AdminNotificationLog({ authToken: _authToken }) {
  const { t } = useLanguage();
  const [channel, setChannel] = useState('');
  const [role, setRole] = useState('');
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const loadNotifications = useCallback(async ({ nextLimit, nextChannel, nextRole, silent = false }) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: String(nextLimit) });
      if (nextChannel) params.set('channel', nextChannel);
      if (nextRole) params.set('role', nextRole);
      const data = await secureFetch(`/api/admin/notifications?${params.toString()}`);
      const rows = data?.notifications || [];
      setItems(rows);
      setHasMore(rows.length >= nextLimit);
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setError(err?.message || t('genericError'));
      setItems([]);
      setHasMore(false);
    } finally {
      if (!silent) setLoading(false);
      setLoadingMore(false);
    }
  }, [t]);

  useEffect(() => {
    const silent = limit > PAGE_SIZE;
    if (silent) setLoadingMore(true);
    loadNotifications({ nextLimit: limit, nextChannel: channel, nextRole: role, silent });
  }, [channel, role, limit, loadNotifications]);

  const channelFilters = useMemo(
    () => CHANNEL_FILTERS.map((entry) => ({ ...entry, label: t(entry.i18nKey) })),
    [t],
  );

  const roleFilters = useMemo(
    () => ROLE_FILTERS.map((entry) => ({
      ...entry,
      label: entry.id ? t(entry.labelKey) : t('notifChannelAll'),
    })),
    [t],
  );

  const filterBtnClass = (active) => cn(
    'rounded-lg border px-3 py-2 text-xs font-semibold transition-colors',
    active
      ? 'border-de-gold bg-de-gold/20 text-de-gold'
      : 'border-de-gold/35 bg-black/20 text-de-muted hover:border-de-gold/60',
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="m-0 flex items-center gap-2 text-lg font-bold text-de-gold">
          <Bell className="h-5 w-5" aria-hidden />
          {t('adminNotifLog')}
        </h2>
      </div>

      <div className="flex flex-wrap gap-2">
        {channelFilters.map((filter) => (
          <button
            key={`channel-${filter.id || 'all'}`}
            type="button"
            className={filterBtnClass(channel === filter.id)}
            onClick={() => {
              setChannel(filter.id);
              setLimit(PAGE_SIZE);
            }}
          >
            {filter.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {roleFilters.map((filter) => (
          <button
            key={`role-${filter.id || 'all'}`}
            type="button"
            className={filterBtnClass(role === filter.id)}
            onClick={() => {
              setRole(filter.id);
              setLimit(PAGE_SIZE);
            }}
          >
            {filter.label}
          </button>
        ))}
      </div>

      <div className="de-ultra-card overflow-hidden">
        {loading && (
          <div className="p-5">
            <p className="m-0 text-sm text-de-muted">{t('loading')}</p>
          </div>
        )}

        {!loading && error && (
          <div className="p-5 text-center">
            <p className="m-0 mb-3 text-sm text-de-accent">{error}</p>
            <button
              type="button"
              onClick={() => loadNotifications({ nextLimit: limit, nextChannel: channel, nextRole: role })}
              className="rounded-lg border border-de-gold/40 bg-de-gold/10 px-4 py-2 text-xs font-semibold text-de-gold"
            >
              {t('retryLabel')}
            </button>
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="p-5 text-center">
            <p className="m-0 text-sm text-de-muted">{t('adminNotifNoData')}</p>
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-de-gold/70">{t('adminNotifColUser')}</TableHead>
                    <TableHead className="text-de-gold/70">{t('adminNotifColRole')}</TableHead>
                    <TableHead className="text-de-gold/70">Channel</TableHead>
                    <TableHead className="text-de-gold/70">Type</TableHead>
                    <TableHead className="text-de-gold/70">Message</TableHead>
                    <TableHead className="text-de-gold/70">Status</TableHead>
                    <TableHead className="text-de-gold/70">Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium text-white">
                        {row.user_name || '—'}
                      </TableCell>
                      <TableCell className="capitalize text-de-muted">
                        {row.user_role || '—'}
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1 text-sm text-de-text">
                          <span aria-hidden>{channelIcon(row.channel)}</span>
                          {row.channel || '—'}
                        </span>
                      </TableCell>
                      <TableCell className="text-de-text">{row.type || '—'}</TableCell>
                      <TableCell className="max-w-[280px] text-de-muted" title={row.message || ''}>
                        {truncateMessage(row.message)}
                      </TableCell>
                      <TableCell className={statusClass(row.status)}>
                        {statusLabel(row.status, t)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-de-muted">
                        {formatTime(row.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {hasMore && (
              <div className="border-t border-de-gold/15 p-4 text-center">
                <button
                  type="button"
                  disabled={loadingMore}
                  onClick={() => setLimit((prev) => prev + PAGE_SIZE)}
                  className={cn(
                    'rounded-lg border border-de-gold/40 bg-de-gold/10 px-4 py-2 text-xs font-semibold text-de-gold',
                    loadingMore && 'cursor-wait opacity-70',
                  )}
                >
                  {loadingMore ? t('loading') : t('notifLoadMore')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
